import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import type { FC } from 'react';
import DaApp from '@wix/da-shared-react/pkg/DaApp';
import SiteHeader from '@wix/da-shared-react/pkg/SiteHeader';
import { RuntimeEnvironment } from '@wix/da-shared-react/pkg/types';
import { NotificationQueueItem } from '@wix/da-shared-react/pkg/Notifications';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import SisuFunnelBanner from '@wix/da-shared-react/pkg/SisuFunnelBanner';
import DrawerLoadable from '@wix/da-feedback-shared/pkg/Drawer/DrawerLoadable';
import FeedbackPageLoadable from '@wix/da-feedback-shared/pkg/FeedbackPage/FeedbackPageLoadable';
import ShowLessNotification from '../ShowLessNotification';
import DywRemoveNotification from '../DywRemoveNotification';
import DywRemoveConfirmationModal from '../DywRemoveConfirmationModal';
import { ModalType, NotificationType } from '../../types/notification';
import { composeModalRenderers } from '@wix/da-shared-react/pkg/Modals/helpers';
import PWABanner from '@wix/da-shared-react/pkg/PWABanner';
import PWAInstallBanner from '@wix/da-shared-react/pkg/PWAInstallBanner';
import { feedbackModalRenderer } from '@wix/da-feedback-shared/pkg/project-tools';
import { adoptsModalRenderer } from '@wix/da-adopts-shared/pkg/project-tools';
import { submitModalRenderer } from '@wix/da-submit-shared/pkg/project-tools';
import PageNetworkbar from '../Page';
import { MuroDepContext } from '@wix/da-react-context/pkg/MuroDepContext';
import MicroMuro from '@wix/da-muro-ui/pkg/components/MuroEditor/preset/Micro';
import MiniMuro from '@wix/da-muro-ui/pkg/components/MuroEditor/preset/Mini';
import MobileMuro from '@wix/da-muro-ui/pkg/components/MuroEditor/preset/Mobile';
import DamzDrawerLoadable from '@wix/da-damz-shared/pkg/components/Drawer/DrawerLoadable';
import DamzPageLoadable from '@wix/da-damz-shared/pkg/components/DamzPage/DamzPageLoadable';
import DamzGlobal from '@wix/da-damz-shared/pkg/redux/ModuleLoader';
import { muroModalRenderer } from '@wix/da-muro-ui/pkg/project-tools';
import { damzModalRenderer } from '@wix/da-damz-shared/pkg/project-tools';

const MuroDepValue = {
  MicroComponent: MicroMuro,
  MiniComponent: MiniMuro,
  MobileComponent: MobileMuro,
};

import s from './App.scss';

export interface Props {
  language: string;
  environment?: RuntimeEnvironment;
  activeSiteHeaderItem?: string;
  sisuFunnelBannerVisible?: boolean;
  PWABannerVisible?: boolean;
  PWAInstallBannerVisible?: boolean;
  showNetworkBarInSiteHeader?: boolean;
  searchTerm?: string;
  searchPath?: string;
  pageType?: string;
}

const renderNotification = (
  notification: NotificationQueueItem,
  removeNotification
) => {
  switch (notification.type) {
    case NotificationType.SHOW_LESS:
      return (
        <ShowLessNotification
          id={notification.id}
          queueName={notification.queueName}
          type={notification.type}
          {...notification.params}
        />
      );
    case NotificationType.REMOVE_DYW:
      return (
        <DywRemoveNotification
          id={notification.id}
          queueName={notification.queueName}
          {...notification.params}
        />
      );
    default:
      return null;
  }
};

const renderModal = composeModalRenderers(
  (modalType: string, requestClose: () => void, params?: any) => {
    switch (modalType) {
      case ModalType.REMOVE_CONFIRM_DYW:
        return (
          <DywRemoveConfirmationModal
            biData={params.biData}
            requestClose={requestClose}
            removeAll={params.removeAll}
          />
        );
      default:
        return null;
    }
  },
  feedbackModalRenderer,
  adoptsModalRenderer,
  submitModalRenderer,
  muroModalRenderer,
  damzModalRenderer as any
);

const App: FC<Props> = ({
  environment,
  language,
  sisuFunnelBannerVisible,
  PWABannerVisible,
  PWAInstallBannerVisible,
  showNetworkBarInSiteHeader,
  activeSiteHeaderItem,
  searchTerm,
  searchPath,
  pageType,
}) => {
  return (
    <MuroDepContext.Provider value={MuroDepValue}>
      <DaApp
        language={language}
        environment={environment}
        notificationsRenderFunc={renderNotification}
        modalsRenderFunc={renderModal}
        ncContextProps={{
          PageComponent: FeedbackPageLoadable,
          DrawerComponent: DrawerLoadable,
        }}
        damzContextProps={{
          GlobalComponent: DamzGlobal,
          DrawerComponent: DamzDrawerLoadable,
          PageComponent: DamzPageLoadable,
        }}
      >
        <ErrorBoundary debugComponent="BrowseApp">
          <SiteHeader
            showNetworkBar={showNetworkBarInSiteHeader}
            activeMainMenuItem={activeSiteHeaderItem}
            searchQuery={searchTerm}
            searchBasePath={searchPath}
          />
          <div className={s['content-container']}>
            <PageNetworkbar />
          </div>
          {sisuFunnelBannerVisible && (
            <SisuFunnelBanner
              visible={sisuFunnelBannerVisible}
              pageType={pageType}
            />
          )}
          {PWABannerVisible && <PWABanner />}
          {PWAInstallBannerVisible && <PWAInstallBanner />}
        </ErrorBoundary>
      </DaApp>
    </MuroDepContext.Provider>
  );
};
App.displayName = 'App';

export default App;
